"use client";

import { useEffect, useRef, useState } from "react";
import { BiteButton } from "./bite-ui";

const SOURCES = { existing: "/bite/report-generation.mp4", fresh: "/bite/report-generation-long.mp4", ending: "/bite/report-ending.mp4" };
export type BiteVideoPhase = "generation" | "ending";
export type BiteGenerationKind = "existing" | "fresh";

export function BiteGenerationVideo({ phase = "generation", generationKind = "existing", onFinished }: { phase?: BiteVideoPhase; generationKind?: BiteGenerationKind; onFinished?: () => void }) {
  const videos = useRef<Record<BiteVideoPhase, HTMLVideoElement | null>>({ generation: null, ending: null });
  const finish = useRef(onFinished);
  useEffect(() => { finish.current = onFinished; }, [onFinished]);
  const [blocked, setBlocked] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const video = videos.current[phase]!;
    let cancelled = false;
    setBlocked(false);
    setFailed(false);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      if (reduced.matches) { video.pause(); if (phase === "ending") finish.current?.(); return; }
      if (document.hidden) { video.pause(); return; }
      if (video.error) { if (phase === "ending") finish.current?.(); else setFailed(true); return; }
      if (video.ended) return;
      video.play().then(() => { if (!cancelled) setBlocked(false); }).catch(() => { if (!cancelled) setBlocked(true); });
    };
    sync();
    reduced.addEventListener("change", sync);
    document.addEventListener("visibilitychange", sync);
    return () => { cancelled = true; video.pause(); reduced.removeEventListener("change", sync); document.removeEventListener("visibilitychange", sync); };
  }, [phase, generationKind]);
  const play = () => videos.current[phase]?.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
  return <div className="bite-generation-video" data-video-phase={phase} data-generation-kind={generationKind}>
    {(["generation", "ending"] as BiteVideoPhase[]).map(kind => <video key={kind} ref={node => { videos.current[kind] = node; }}
      src={SOURCES[kind === "generation" ? generationKind : "ending"]} hidden={phase !== kind} muted playsInline preload="auto" aria-hidden="true"
      loop={kind === "generation" && phase === "generation" && generationKind === "fresh"}
      onEnded={() => { if (phase === "ending" && kind === phase) finish.current?.(); }}
      onError={() => { if (kind !== phase) return; if (kind === "ending") finish.current?.(); else setFailed(true); }} />)}
    {failed ? <p className="bite-generation-video__notice">영상을 불러오지 못했습니다. 보고서는 계속 생성됩니다.</p>
      : blocked && <BiteButton variant="secondary" className="bite-generation-video__play" onClick={play}>영상 재생</BiteButton>}
  </div>;
}
